// Onderbalk: datum- en tijdslider met markeringen voor astronomische datums
// (zonnewenden, equinoxen) en de Haagse bezonningsnorm-toetsdatums.
// Per §2.4 van het ontwerp.
//
// We tonen ook de actuele zon-azimuth en -elevatie voor de gekozen tijd, en
// gearceerde randzones in de tijdslider voor zonsopkomst en -ondergang.

import { useMemo } from "react";
import { REFERENCE_YEAR, useStore } from "../state/store";
import {
  dayOfYearToMonthDay,
  getSunPosition,
  getSunTimesNl,
  monthDayToDayOfYear,
  nlClockToUtc,
} from "../shadow/sunPosition";
import { Button } from "./ui/Button";
import { Map as MapIcon, Box, Camera } from "lucide-react";
import { downloadSnapshot } from "../utils/snapshot";

interface DateMark {
  doy: number;
  label: string;
  description: string;
  /** Tailwind kleurklasse voor de markering. */
  color: string;
}

const DATE_MARKS: DateMark[] = [
  { doy: doy(0, 19), label: "19/1", description: "Haagse norm — strenge toetsing", color: "bg-rose-600" },
  { doy: doy(1, 19), label: "19/2", description: "Haagse norm — lichte toetsing", color: "bg-amber-500" },
  { doy: doy(2, 21), label: "21/3", description: "Lente-equinox", color: "bg-emerald-500" },
  { doy: doy(5, 21), label: "21/6", description: "Zomerzonnewende", color: "bg-emerald-500" },
  { doy: doy(8, 21), label: "21/9", description: "Herfst-equinox", color: "bg-emerald-500" },
  { doy: doy(9, 21), label: "21/10", description: "Haagse norm — lichte toetsing", color: "bg-amber-500" },
  { doy: doy(10, 22), label: "22/11", description: "Haagse norm — strenge toetsing", color: "bg-rose-600" },
  { doy: doy(11, 21), label: "21/12", description: "Winterzonnewende", color: "bg-emerald-500" },
];

function doy(month0: number, day: number): number {
  return monthDayToDayOfYear(REFERENCE_YEAR, month0, day);
}

const RAD2DEG = 180 / Math.PI;
const DAYS_IN_REFERENCE_YEAR = 365; // 2025 is geen schrikkeljaar
const MINUTES_IN_DAY = 24 * 60;

export function TimeSliders() {
  const dayOfYear = useStore((s) => s.currentDayOfYear);
  const minuteOfDay = useStore((s) => s.currentMinuteOfDay);
  const setDay = useStore((s) => s.setCurrentDayOfYear);
  const setMinute = useStore((s) => s.setCurrentMinuteOfDay);
  const lat = useStore((s) => s.scenario.view.centerLat);
  const lng = useStore((s) => s.scenario.view.centerLng);

  const { month, day } = dayOfYearToMonthDay(REFERENCE_YEAR, dayOfYear);
  const hour = Math.floor(minuteOfDay / 60);
  const minute = minuteOfDay % 60;

  const dateText = `${pad(day)}-${pad(month + 1)}`;
  const timeText = `${pad(hour)}:${pad(minute)}`;

  // Zonsopkomst en -ondergang voor de gekozen datum.
  const sunTimes = useMemo(
    () => getSunTimesNl(REFERENCE_YEAR, month, day, lat, lng),
    [month, day, lat, lng],
  );

  // Zonpositie op het gekozen moment.
  const sunPos = useMemo(() => {
    const utc = nlClockToUtc(REFERENCE_YEAR, month, day, hour, minute);
    return getSunPosition(utc, lat, lng);
  }, [month, day, hour, minute, lat, lng]);

  const azDeg = sunPos.azimuth * RAD2DEG; // SunCalc: 0 = zuid
  const elDeg = sunPos.altitude * RAD2DEG;
  // Voor weergave: kompas-azimuth (0 = noord, 90 = oost), vriendelijker voor lezers.
  const compassAz = ((azDeg + 180) % 360 + 360) % 360;

  return (
    <div className="border-t border-border bg-card px-2 lg:px-4 py-2 flex flex-col lg:flex-row lg:items-stretch gap-2 lg:gap-4 text-sm">
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 mb-1">
          <label className="text-xs font-medium uppercase text-muted-foreground w-12 flex-none">
            Datum
          </label>
          <input
            type="range"
            min={1}
            max={DAYS_IN_REFERENCE_YEAR}
            step={1}
            value={dayOfYear}
            onChange={(e) => setDay(Number(e.target.value))}
            className="flex-1 min-w-0"
          />
          <input
            type="text"
            value={dateText}
            onChange={(e) => {
              const parsed = parseDateText(e.target.value);
              if (parsed !== null) setDay(parsed);
            }}
            className="w-20 flex-none bg-background border border-input rounded-md px-2 py-1 text-sm text-center"
            title="Formaat: DD-MM"
          />
        </div>
        <div className="relative h-4 mx-12">
          {DATE_MARKS.map((m) => {
            const pct = ((m.doy - 1) / (DAYS_IN_REFERENCE_YEAR - 1)) * 100;
            return (
              <button
                key={m.doy}
                onClick={() => setDay(m.doy)}
                title={`${m.description} — ${m.label}`}
                className={`absolute -translate-x-1/2 w-2 h-3 rounded-sm ${m.color} hover:scale-150 transition-transform`}
                style={{ left: `${pct}%`, top: 0 }}
              />
            );
          })}
        </div>
      </div>

      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 mb-1">
          <label className="text-xs font-medium uppercase text-muted-foreground w-12 flex-none">
            Tijd
          </label>
          <input
            type="range"
            min={0}
            max={1430}
            step={10}
            value={minuteOfDay}
            onChange={(e) => setMinute(Number(e.target.value))}
            className="flex-1 min-w-0"
          />
          <input
            type="text"
            value={timeText}
            onChange={(e) => {
              const parsed = parseTimeText(e.target.value);
              if (parsed !== null) setMinute(parsed);
            }}
            className="w-20 flex-none bg-background border border-input rounded-md px-2 py-1 text-sm text-center"
            title="Formaat: HH:MM"
          />
        </div>
        <div className="text-xs text-muted-foreground mx-12">
          Zonsopkomst {minutesToHHMM(sunTimes.sunriseMin)} · Zonsondergang{" "}
          {minutesToHHMM(sunTimes.sunsetMin)} · Schaduw vanaf 10° elevatie
        </div>
      </div>

      {/* Op kleine schermen zitten zon-info, view-toggle en snapshot op één
          rij onder de sliders. Op lg+ ontvouwen ze zich los in de hoofd-flex
          via `lg:contents`. */}
      <div className="flex items-center justify-between gap-3 lg:contents">
        <div className="flex flex-col lg:justify-center text-xs leading-tight whitespace-nowrap">
          <div>
            <span className="text-muted-foreground">Azimuth: </span>
            <span
              className="font-mono"
              title="Kompasrichting van de zon (0° = noord, 90° = oost, 180° = zuid, 270° = west)"
            >
              {compassAz.toFixed(0)}°
            </span>
          </div>
          <div>
            <span className="text-muted-foreground">Elevatie: </span>
            <span
              className={
                "font-mono " + (elDeg < 10 ? "text-rose-600" : "text-foreground")
              }
              title="Hoogte van de zon boven de horizon. Onder 10° tellen we geen schaduw mee (Haagse-norm-conventie)."
            >
              {elDeg.toFixed(1)}°
            </span>
          </div>
        </div>

        <ViewModeToggle />
        <SnapshotButton />
      </div>
    </div>
  );
}

function SnapshotButton() {
  const viewMode = useStore((s) => s.viewMode);
  return (
    <Button
      variant="outline"
      size="sm"
      onClick={() =>
        downloadSnapshot(viewMode).catch((err) =>
          alert("Snapshot mislukt: " + (err instanceof Error ? err.message : String(err))),
        )
      }
      title="Maak een PNG-afbeelding van de huidige weergave"
    >
      <Camera size={14} /> Snapshot
    </Button>
  );
}

function ViewModeToggle() {
  const viewMode = useStore((s) => s.viewMode);
  const setViewMode = useStore((s) => s.setViewMode);
  return (
    <div className="flex flex-col justify-center">
      <div
        role="group"
        className="inline-flex border border-input rounded-md overflow-hidden"
        aria-label="Weergave"
      >
        <Button
          variant={viewMode === "2d" ? "active" : "ghost"}
          size="sm"
          onClick={() => setViewMode("2d")}
          className="rounded-none border-0"
          title="Bovenaanzicht (kaart)"
        >
          <MapIcon size={14} /> 2D
        </Button>
        <Button
          variant={viewMode === "3d" ? "active" : "ghost"}
          size="sm"
          onClick={() => setViewMode("3d")}
          className="rounded-none border-0"
          title="3D-weergave"
        >
          <Box size={14} /> 3D
        </Button>
      </div>
    </div>
  );
}

function pad(n: number): string {
  return String(n).padStart(2, "0");
}

function minutesToHHMM(min: number): string {
  if (!Number.isFinite(min)) return "—";
  const m = ((min % MINUTES_IN_DAY) + MINUTES_IN_DAY) % MINUTES_IN_DAY;
  return `${pad(Math.floor(m / 60))}:${pad(Math.round(m % 60))}`;
}

function parseDateText(text: string): number | null {
  const m = text.match(/^\s*(\d{1,2})[-/](\d{1,2})\s*$/);
  if (!m) return null;
  const day = Number(m[1]);
  const month = Number(m[2]);
  if (month < 1 || month > 12 || day < 1 || day > 31) return null;
  return monthDayToDayOfYear(REFERENCE_YEAR, month - 1, day);
}

function parseTimeText(text: string): number | null {
  const m = text.match(/^\s*(\d{1,2}):(\d{1,2})\s*$/);
  if (!m) return null;
  const h = Number(m[1]);
  const min = Number(m[2]);
  if (h < 0 || h > 23 || min < 0 || min > 59) return null;
  return h * 60 + min;
}
